import type { Standard } from '@netpurpose/types'
import { valueIsDefined } from '@netpurpose/utils'
import type { UseQueryResult } from '@tanstack/react-query'
import type {
  DataType,
  EntityImpactData,
  GetEntityDashboardDataResponse,
  GetPortfolioDashboardDataResponse,
  PortfolioImpactData,
  ReportingStandardMajorDetails,
} from '../../../generated/facts'
import type { BackendDataValue } from '../../../models/dataValue'
import { type Camelize, snakeToCamelKeys } from '../../../utils'
import { parseImpactDataValue } from './parseImpactDataValue'
import { transformHighYoyReasons } from './transformEntity'

const transformStandard = ({
  majorCode,
  standardClass,
  link,
  colour,
  fullCode,
  description,
  ...standard
}: Camelize<ReportingStandardMajorDetails> & {
  fullCode?: string | null
  description?: string | null
}): Standard => ({
  id: parseInt(majorCode as string, 10),
  standardClass,
  majorCode: majorCode || undefined,
  fullCode: fullCode || undefined,
  description: description || undefined,
  link: link || undefined,
  colour: colour || undefined,
  ...standard,
})

const transformStandards = (
  sdgs: Camelize<PortfolioImpactData['sdg_goals']> | Camelize<PortfolioImpactData['sdg_targets']>,
) => sdgs?.map(transformStandard)

const isBackendDataValue = (val: unknown[] | undefined | null): val is BackendDataValue =>
  val ? (val[0] === null || typeof val[0] === 'number') && typeof val[1] === 'string' : false

const parseImpactDataValueWithNarrowedDownType = (value: unknown[] | undefined | null) => {
  const valueWithNarrowedDownType = isBackendDataValue(value) ? value : null
  return parseImpactDataValue(valueWithNarrowedDownType)
}

export const transformPortfolioImpactData = <
  PortfolioImpactDataProps extends Camelize<PortfolioImpactData>,
>({
  questionId,
  displayThemes,
  intensity,
  revenueIntensity,
  yearOnYear,
  goal,
  impactLevel,
  sdgGoals,
  sdgTargets,
  isContributing,
  isNd,
  weightedPortfolioImpact,
  unweightedPortfolioImpact,
  ...rest
}: PortfolioImpactDataProps) => ({
  intensity: parseImpactDataValueWithNarrowedDownType(intensity),
  revenueIntensity: parseImpactDataValueWithNarrowedDownType(revenueIntensity),
  yearOnYear: parseImpactDataValueWithNarrowedDownType(yearOnYear),
  goal,
  impactLevel,
  sdgTargets: sdgTargets ? transformStandards(sdgTargets) : undefined,
  weightedPortfolioImpact: parseImpactDataValueWithNarrowedDownType(weightedPortfolioImpact),
  unweightedPortfolioImpact: parseImpactDataValueWithNarrowedDownType(unweightedPortfolioImpact),
  displayThemes,
  questionId,
  isNd,
  isContributing,
  ...rest,
})

const parseFactoidOrigin = (factoidOrigin: Camelize<EntityImpactData>['factoidOrigin']) =>
  factoidOrigin
    ? {
        ...factoidOrigin,
        factoidId:
          factoidOrigin?.dataType === 'Estimated' && typeof factoidOrigin?.factoidId === 'string'
            ? // The generated types suggests this should be an array, but it's actually a string
              // in the format "foo<sep>bar".
              factoidOrigin.factoidId.split('<sep>')
            : factoidOrigin.factoidId,
        description: factoidOrigin?.description,
        dataType: (factoidOrigin?.dataType === 'Calculated'
          ? 'Standardised'
          : // Ideally we should just drop the checks for "Standardised" everywhere
            // and just use the original "Calculated" value.
            factoidOrigin?.dataType) as Exclude<DataType, 'Calculated'> | 'Standardised',
      }
    : undefined

export const transformEntityImpactData = <
  EntityImpactDataProps extends Camelize<EntityImpactData>,
>({
  questionId,
  canonicalId,
  displayThemes,
  absolute,
  intensity,
  revenueIntensity,
  yearOnYear,
  goal,
  impactLevel,
  sdgGoals,
  sdgTargets,
  isContributing,
  isNd,
  factoidOrigin,
  isHighYoy,
  highYoyReasons,
  ...rest
}: EntityImpactDataProps) => ({
  questionId,
  canonicalId,
  displayThemes,
  absolute: parseImpactDataValueWithNarrowedDownType(absolute),
  intensity: parseImpactDataValueWithNarrowedDownType(intensity),
  revenueIntensity: parseImpactDataValueWithNarrowedDownType(revenueIntensity),
  yearOnYear: parseImpactDataValueWithNarrowedDownType(yearOnYear),
  goal,
  impactLevel,
  isNd,
  sdgTargets: sdgTargets ? transformStandards(sdgTargets) : undefined,
  isContributing,
  factoidOrigin: parseFactoidOrigin(factoidOrigin),
  isHighYoY: isHighYoy || false,
  highYoYReasons: transformHighYoyReasons(highYoyReasons),
  ...rest,
})

const isPortfolioImpactDataBE = (
  data: PortfolioImpactData | EntityImpactData,
): data is PortfolioImpactData =>
  (data as PortfolioImpactData).unweighted_portfolio_impact !== undefined

export const standardiseImpactDataForCompare = ({
  response,
}: {
  response: UseQueryResult<
    [string, GetPortfolioDashboardDataResponse | GetEntityDashboardDataResponse] | undefined,
    Error
  >[]
}) => {
  const data = response.map((r) => r.data).filter(valueIsDefined)

  return data
    ? Object.fromEntries(
        data.map(([snapshotOrEntityId, impactData]) => [
          snapshotOrEntityId,
          impactData.map((d) => {
            const { value, unit } = parseImpactDataValueWithNarrowedDownType(
              isPortfolioImpactDataBE(d) ? d.unweighted_portfolio_impact : d.absolute,
            )
            return {
              // BE generated types are incorrect, question_id will always be present.
              questionId: d.question_id as number,
              displayName: d.display_name,
              value,
              unitName: unit,
              intensity: parseImpactDataValueWithNarrowedDownType(d.intensity).value,
              revenueIntensity: parseImpactDataValueWithNarrowedDownType(d.revenue_intensity).value,
              yearOnYear: parseImpactDataValueWithNarrowedDownType(d.year_on_year).value,
              goal: d.goal,
              isNd: d.is_nd,
              displayThemes: d.display_themes,
              impactLevel: d.impact_level,
              displayPriority: d.display_priority,
              ...(isPortfolioImpactDataBE(d)
                ? {}
                : {
                    factoidOrigin: parseFactoidOrigin(snakeToCamelKeys(d.factoid_origin)),
                    isDerived: d.is_derived,
                  }),
              sdgTargets: d.sdg_targets
                ? transformStandards(snakeToCamelKeys(d.sdg_targets))
                : undefined,
              sdgRevenuePercentage: d.sdg_revenue_percentage,
            }
          }),
        ]),
      )
    : undefined
}

export type ImpactDataForCompare = NonNullable<ReturnType<typeof standardiseImpactDataForCompare>>
